// The nav model.
//
// An entry is either a LEAF (has `path`) or a GROUP (has `children`). A group is
// not itself a destination — clicking it opens its children — so it carries no
// `path` of its own, and its `id` is what the desktop Tabs uses as a value.
export const menu = [
  {
    id: 'bot_tab_main_link',
    name: 'Dashboard',
    path: '/',
  },
  {
    id: 'cex_first_tab_main_link',
    name: 'Cex First',
    path: '/cex-first',
  },
  {
    id: 'bot_details_tab_main_link',
    name: 'Bots',
    path: '/bot-details',
  },
  {
    id: 'signals_tab_main_link',
    name: 'Signals',
    path: '/bot-signals',
  },
  {
    id: 'opportunities_tab_main_link',
    name: 'Opportunities',
    path: '/bot-opportunities',
  },
  {
    id: 'balances_tab_main_link',
    name: 'Balances',
    path: '/balances',
  },
  {
    id: 'positions_tab_main_link',
    name: 'Positions',
    path: '/positions',
  },
  {
    id: 'seeker_tab_main_link',
    name: 'Seeker',
    path: '/seeker-rules',
  },
  {
    id: 'balance_leveling_tab_main_link',
    name: 'Balance leveling',
    path: '/balance-leveling',
  },
  {
    id: 'MarketVolatility_tab_main_link',
    name: 'Market Volatility',
    path: '/market-volatility',
  },
  {
    id: 'deployments_tab_main_link',
    name: 'Deployments',
    path: '/deployments',
  },
  // Route engine. A top-level group rather than a child of Settings: during the rollout
  // these two are watched daily, which is the opposite of the "edited rarely, read never"
  // test that put the others under Settings.
  {
    id: 'route_engine_group_main_link',
    name: 'Route engine',
    children: [
      {
        id: 'route_policies_tab_main_link',
        name: 'Policies',
        path: '/route-policies',
      },
      {
        id: 'route_engine_rollout_tab_main_link',
        name: 'Rollout',
        path: '/route-policies/rollout',
      },
      {
        id: 'route_insights_tab_main_link',
        name: 'Insights',
        path: '/route-insights',
      },
      // Its own entry rather than only a link off Insights: during the canary the raw event
      // trail is what gets opened directly, and activeNavLabel resolves the longest match,
      // so a candidate page still labels itself "Findings" rather than "Insights".
      {
        id: 'route_findings_tab_main_link',
        name: 'Findings',
        path: '/route-insights/findings',
      },
      // The store the four above are read out of. It shares no prefix with them, so
      // activeNavValue and activeNavLabel need no change to resolve it.
      {
        id: 'route_clickhouse_tab_main_link',
        name: 'ClickHouse',
        path: '/route-engine/clickhouse',
      },
    ],
  },
  // Configuration lives here: the screens an operator edits rarely and
  // reads never, pulled out of the row of screens they watch all day. The ids
  // of the children are unchanged from when they were top-level tabs, so any
  // selector or bookmark keyed on them still resolves.
  {
    id: 'settings_group_main_link',
    name: 'Settings',
    children: [
      {
        id: 'settings_tab_main_link',
        name: 'Nonce',
        path: '/settings/nonce',
      },
      {
        id: 'config_tab_main_link',
        name: 'Config',
        path: '/settings/config',
      },
      {
        id: 'dex_tokens_tab_main_link',
        name: 'Tokens',
        path: '/dex-tokens',
      },
      // Was the bottom half of the Tokens page. Its own entry now, next to the page it was
      // split out of: the two are edited in separate sittings and reached separately.
      {
        id: 'dex_pairs_tab_main_link',
        name: 'Pairs',
        path: '/dex-pairs',
      },
      {
        id: 'allowances_tab_main_link',
        name: 'Allowances',
        path: '/allowances',
      },
      {
        id: 'exchanges_tab_main_link',
        name: 'Exchanges',
        path: '/exchanges',
      },
      {
        id: 'Networks_tab_main_link',
        name: 'Networks',
        path: '/networks',
      },
    ],
  },
]

export const isGroup = item => Array.isArray(item.children)

// Several routes are react-router wildcards (`settings/*`, `bot-details/*`), so
// an exact pathname comparison marks the tab unselected as soon as the user is
// one level deep. Prefix-match, but not for '/' — that is every path's prefix.
export const isPathActive = (path, pathname) =>
  pathname === path || (path !== '/' && pathname.startsWith(`${path}/`))

// Which Tabs value should read as selected.
//
// Returns `false` — MUI's "nothing selected" — when the pathname belongs to no
// nav entry. That case is real (/bookkeeping, /brokers, /top-deals, /gallery and
// every 404 are reachable but not in the menu) and previously passed the raw
// pathname to Tabs as a value no Tab had, which MUI warns about at runtime.
export const activeNavValue = pathname => {
  for (const item of menu) {
    if (isGroup(item)) {
      if (item.children.some(child => isPathActive(child.path, pathname))) return item.id
    } else if (isPathActive(item.path, pathname)) {
      return item.path
    }
  }

  return false
}

// The label for the collapsed mobile menu. A child route shows its OWN name,
// not the group's — "Allowances" is more use than "Settings" when it is the
// only thing on screen telling you where you are.
export const activeNavLabel = pathname => {
  // LONGEST match, not first. '/route-policies' is a prefix of '/route-policies/rollout',
  // so a first-match scan would label the rollout page "Policies" — and the whole reason
  // this shows the child's own name rather than the group's is that "Allowances" is more
  // use than "Settings" when it is the only thing on screen telling you where you are.
  // No group had nested paths before, so this changes nothing for the existing entries.
  let best = null

  for (const item of menu) {
    const candidates = isGroup(item) ? item.children : [item]

    for (const entry of candidates) {
      if (isPathActive(entry.path, pathname) && (!best || entry.path.length > best.path.length)) {
        best = entry
      }
    }
  }

  return best ? best.name : ''
}
